import { buildSearchParams, urlBuilder } from '@starjack/utils';
import { BackendType, FrontendType, ObjectType } from '@starjack/utils';
import { baseApi } from '@starjack/api';
import { convertVoucherToFrontend, Voucher } from '../model/voucher.types';
import { getVouchersExportSearchParams } from '../lib/voucherExport';

export type GetVouchersSearchParams = {
    code?: string;
    page?: number;
    size?: number;
};

export const convertGetVouchersSearchParamsToBackend = (
    params: GetVouchersSearchParams
): GetVouchersSearchParams => {
    return params;
};

export type GetVouchersResponse<T extends ObjectType = FrontendType> = {
    content: Voucher<T>[];
    size: number;
    totalElements: number;
    totalPages: number;
};

const voucherUrls = {
    vouchers: urlBuilder('order', 'sold-vouchers'),
    vouchersExport: urlBuilder('order', 'sold-vouchers/export.xlsx'),
};

export const getVouchersExportUrl = (params: GetVouchersSearchParams) =>
    `${voucherUrls.vouchersExport}${buildSearchParams(
        convertGetVouchersSearchParamsToBackend(
            getVouchersExportSearchParams(params)
        )
    )}`;

export const voucherApi = baseApi
    .enhanceEndpoints({ addTagTypes: ['VOUCHERS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getVouchers: builder.query<
                GetVouchersResponse,
                GetVouchersSearchParams
            >({
                query: (params: GetVouchersSearchParams) => {
                    return `${voucherUrls.vouchers}${buildSearchParams(
                        convertGetVouchersSearchParamsToBackend(params)
                    )}`;
                },
                transformResponse: (
                    response: GetVouchersResponse<BackendType>
                ): GetVouchersResponse => {
                    return {
                        ...response,
                        content: response.content.map(convertVoucherToFrontend),
                    };
                },
                providesTags: ['VOUCHERS'],
            }),

            postVoucherCancelInsurance: builder.mutation<
                unknown,
                { voucherId: string }
            >({
                query: ({ voucherId }: { voucherId: string }) => ({
                    url: `${voucherUrls.vouchers}/${voucherId}/cancel-insurance`,
                    method: 'POST',
                    body: { voucherId },
                }),
                invalidatesTags: ['VOUCHERS'],
            }),

            postVoucherRevokeInsurance: builder.mutation<
                unknown,
                { voucherId: string }
            >({
                query: ({ voucherId }: { voucherId: string }) => ({
                    url: `${voucherUrls.vouchers}/${voucherId}/revoke-insurance`,
                    method: 'POST',
                    body: { voucherId },
                }),
                invalidatesTags: ['VOUCHERS'],
            }),

            postVoucherCancelInsuranceFromInsurance: builder.mutation<
                unknown,
                { voucherId: string }
            >({
                query: ({ voucherId }: { voucherId: string }) => ({
                    url: `${voucherUrls.vouchers}/${voucherId}/embargo-insurance`,
                    method: 'POST',
                    body: { voucherId },
                }),
                invalidatesTags: ['VOUCHERS'],
            }),
        }),
    });

export const {
    useGetVouchersQuery,
    usePostVoucherRevokeInsuranceMutation,
    usePostVoucherCancelInsuranceMutation,
    usePostVoucherCancelInsuranceFromInsuranceMutation,
} = voucherApi;
